import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  ChevronLeft, Zap, AlertCircle, QrCode, Building2, Loader2,
  Copy, Clock, CheckCircle2, RefreshCw, Phone, Gamepad2, ShoppingBag,
  Wallet
} from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import theme from '../theme.config';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PAYMENT_METHODS = [
  { id: 'qris', name: 'QRIS', icon: QrCode, desc: 'GoPay, OVO, Dana, LinkAja' },
  { id: 'va_bni', name: 'VA BNI', icon: Building2, desc: 'BNI Transfer', channel: 'bni' },
  { id: 'va_bri', name: 'VA BRI', icon: Building2, desc: 'BRI Transfer', channel: 'bri' },
  { id: 'va_mandiri', name: 'VA Mandiri', icon: Building2, desc: 'Mandiri Transfer', channel: 'mandiri' },
  { id: 'va_cimb', name: 'VA CIMB', icon: Building2, desc: 'CIMB Transfer', channel: 'cimb' },
];

const BANK_INFO = {
  bni: { name: 'BNI', color: '#f15a22' },
  bri: { name: 'BRI', color: '#00529c' },
  mandiri: { name: 'Mandiri', color: '#003366' },
  cimb: { name: 'CIMB', color: '#ec1c24' },
};

// Default fallback config
const DEFAULT_CFG = {
  type: 'game_id',
  id_label: 'User ID',
  id_placeholder: 'Enter User ID',
  id_required: true,
  show_id2: false,
  instruction: 'Enter your target account info.',
  success_label: 'User ID',
};

export default function DigiFlazzProduct() {
  const { brandSlug } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();

  const [brand, setBrand] = useState(null);
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState('games');
  const [cfg, setCfg] = useState(DEFAULT_CFG);
  const [loading, setLoading] = useState(true);
  const [selectedSku, setSelectedSku] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [userId, setUserId] = useState('');
  const [serverId, setServerId] = useState('');
  const [email, setEmail] = useState(user?.email || '');
  const [submitting, setSubmitting] = useState(false);

  const [paymentData, setPaymentData] = useState(null);
  const [paymentStep, setPaymentStep] = useState('form');
  const [orderStatus, setOrderStatus] = useState(null);

  useEffect(() => { fetchBrand(); }, [brandSlug]);
  useEffect(() => { if (user?.email) setEmail(user.email); }, [user]);

  // Poll payment status
  useEffect(() => {
    if (paymentStep !== 'paying' || !paymentData?.orderId) return;
    let active = true;
    const poll = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/payment/status/${paymentData.orderId}`);
        if (!active) return;
        setOrderStatus(res.data);
        if (res.data.status === 'paid' || res.data.status === 'completed') {
          setPaymentStep('success');
          return;
        }
        if (res.data.status === 'failed' || res.data.status === 'cancelled') {
          setPaymentStep('failed');
          return;
        }
      } catch {}
      if (active) setTimeout(poll, 4000);
    };
    setTimeout(poll, 4000);
    return () => { active = false; };
  }, [paymentStep, paymentData?.orderId]);

  // Keep polling on success for SN
  useEffect(() => {
    if (paymentStep !== 'success' || !paymentData?.orderId) return;
    if (orderStatus?.digiflazz_sn && orderStatus?.status === 'completed') return;
    let active = true;
    const poll = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/payment/status/${paymentData.orderId}`);
        if (!active) return;
        setOrderStatus(res.data);
        if (res.data.digiflazz_sn && res.data.status === 'completed') return;
      } catch {}
      if (active) setTimeout(poll, 5000);
    };
    setTimeout(poll, 3000);
    return () => { active = false; };
  }, [paymentStep, paymentData?.orderId, orderStatus?.digiflazz_sn]);

  const fetchBrand = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/biller/catalog/${brandSlug}`);
      setBrand(res.data);
      setProducts(res.data.products || []);
      setCategory(res.data.category || 'games');
      if (res.data.input_config) setCfg(res.data.input_config);
    } catch {
      toast.error('Product not found');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedSku) return toast.error('Please select an item first');
    if (!selectedPayment) return toast.error('Please select a payment method');
    if (cfg.id_required && !userId) return toast.error(`Enter ${cfg.id_label}`);
    if (!email) return toast.error('Enter email');

    setSubmitting(true);
    try {
      const orderRes = await axios.post(`${API_URL}/api/orders/digiflazz`, {
        brand: brand.brand,
        sku_code: selectedSku.sku,
        product_name: selectedSku.name,
        game_user_id: userId,
        game_server_id: serverId || null,
        email,
        payment_method: selectedPayment.id,
        price: selectedSku.price,
      });
      const orderId = orderRes.data.order.id;

      const isQris = selectedPayment.id === 'qris';
      const payRes = await axios.post(`${API_URL}/api/payment/create`, {
        order_id: orderId,
        amount: selectedSku.price,
        customer_name: email.split('@')[0],
        customer_email: email,
        payment_method: isQris ? 'qris' : 'va',
        va_channel: selectedPayment.channel || 'bni',
        item_name: `${brand?.brand} - ${selectedSku.name}`,
        digiflazz_sku: selectedSku.sku,
        customer_game_id: cfg.show_id2 && serverId ? `${userId}${serverId}` : userId,
      });

      if (payRes.data.success) {
        setPaymentData({ ...payRes.data.data, orderId });
        setPaymentStep('paying');
        toast.success('Payment successfully created!');
      } else {
        toast.error(payRes.data.message || 'Failed to create payment');
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to process order');
    } finally {
      setSubmitting(false);
    }
  };

  const copyText = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied!');
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-16 flex items-center justify-center bg-[#09090b]">
        <div className="animate-spin w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!brand) return null;

  // ==================== FAILED / CANCELLED ====================
  if (paymentStep === 'failed') {
    return (
      <div className="min-h-screen pt-24 pb-12 bg-[#09090b] text-zinc-50 relative flex items-center">
        <div className="absolute inset-0 bg-[radial-gradient(#1c1c1f_1px,transparent_1px)] [background-size:16px_16px] opacity-10 pointer-events-none" />
        <div className="max-w-lg mx-auto px-4 text-center z-10">
          <div className="w-16 h-16 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full border border-red-500/30 bg-red-500/5">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
            <span className="font-mono text-[10px] text-red-400 font-semibold tracking-wider uppercase">Transaction Failed</span>
          </div>
          <h1 className="font-bold text-xl text-white mb-2">
            {orderStatus?.status === 'cancelled' ? 'Transaction Cancelled' : 'Transaction Failed'}
          </h1>
          <p className="text-zinc-400 text-sm mb-8">
            {orderStatus?.status === 'cancelled'
              ? 'The transaction was cancelled or the payment session expired. Please try again.'
              : 'A routing failure occurred during payment settle. Please try again.'}
          </p>
          <div className="flex gap-3 justify-center">
            <Button variant="outline" className="border-zinc-800 text-white hover:bg-zinc-900 text-xs"
              onClick={() => { setPaymentStep('form'); setPaymentData(null); setOrderStatus(null); }}>
              <RefreshCw className="w-4 h-4 mr-2" /> Try Again
            </Button>
            <Button className="bg-emerald-600 hover:bg-emerald-500 text-white font-semibold text-xs" onClick={() => navigate('/')}>
              Back to Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ==================== SUCCESS ====================
  if (paymentStep === 'success') {
    const isVoucher = category === 'voucher';
    const hasSn = orderStatus?.digiflazz_sn;
    const topupFailed = orderStatus?.topup_status === 'failed';
    const isProcessing = !hasSn && !topupFailed;

    return (
      <div className="min-h-screen pt-24 pb-12 bg-[#09090b] text-zinc-50 relative flex items-center">
        <div className="absolute inset-0 bg-[radial-gradient(#1c1c1f_1px,transparent_1px)] [background-size:16px_16px] opacity-10 pointer-events-none" />
        <div className="max-w-lg mx-auto px-4 text-center z-10">

          {/* Header - changes based on state */}
          {isProcessing ? (
            <>
              <div className="w-16 h-16 rounded-full bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center mx-auto mb-6">
                <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
              </div>
              <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full border border-cyan-500/30 bg-cyan-500/5">
                <span className="w-1.5 h-1.5 rounded-full bg-cyan-500 animate-pulse" />
                <span className="font-mono text-[10px] text-cyan-400 font-semibold tracking-wider uppercase">Processing Payload</span>
              </div>
              <h1 className="font-bold text-xl text-white mb-2">Processing Transaction...</h1>
              <p className="text-zinc-400 text-sm mb-6">
                Payment verified successfully! Routing the transaction payload to the target destination.
              </p>
              <div className="bg-cyan-500/5 border border-cyan-500/20 rounded-xl p-4 mb-6">
                <div className="flex items-center justify-center gap-2 text-cyan-400 text-xs font-bold mb-1">
                  <AlertCircle className="w-4 h-4" />
                  STAY ON THIS PAGE
                </div>
                <p className="text-[10px] text-zinc-550">
                  Our automated router is delivering the payload and resolving the serial number response.
                </p>
              </div>

              {/* Processing dots animation */}
              <div className="flex justify-center gap-1.5 mb-6">
                {[0,1,2,3,4].map(i => (
                  <div key={i} className="w-1.5 h-1.5 rounded-full bg-cyan-500 animate-pulse" style={{ animationDelay: `${i * 0.2}s` }} />
                ))}
              </div>
            </>
          ) : (
            <>
              <div className="w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-8 h-8 text-emerald-400" />
              </div>
              <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full border border-emerald-500/30 bg-emerald-500/5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                <span className="font-mono text-[10px] text-emerald-400 font-semibold tracking-wider uppercase">Route Settled</span>
              </div>
              <h1 className="font-bold text-xl text-white mb-2">
                {topupFailed ? 'Manual Processing Required' : 'Transaction Complete!'}
              </h1>
              <p className="text-zinc-400 text-sm mb-6">
                {topupFailed ? 'Payment verified, but auto-routing met a network delay. Contact Admin.' :
                 `Digital cargo has been successfully settled by NeuroVanta.`}
              </p>
            </>
          )}

          {/* SN / Voucher Code */}
          {hasSn && (
            <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-5 mb-6">
              <p className="text-[10px] text-emerald-400 mb-2 uppercase tracking-wider font-semibold">
                {isVoucher ? 'DECRYPTED VOUCHER KEY' : 'SERIAL NUMBER'}
              </p>
              <div className="flex items-center justify-center gap-3">
                <span className="font-mono text-white font-bold text-base break-all">{orderStatus.digiflazz_sn}</span>
                <button onClick={() => copyText(orderStatus.digiflazz_sn)}
                  className="p-1.5 rounded-lg bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 flex-shrink-0 border border-emerald-500/20">
                  <Copy className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}

          {/* Top-up failed warning */}
          {topupFailed && (
            <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-4 mb-6">
              <p className="text-red-400 text-xs font-bold">ROUTING FAILURE</p>
              {orderStatus?.topup_error && (
                <p className="text-[10px] text-red-400/80 mt-1 font-mono">{orderStatus.topup_error}</p>
              )}
              <p className="text-[10px] text-zinc-500 mt-2">Please contact administration support with your Order ID for manual completion.</p>
            </div>
          )}

          {/* Order details */}
          <div className="bg-zinc-900/30 rounded-xl p-6 border border-zinc-850 mb-6 text-left">
            <div className="flex items-center gap-3 mb-4">
              <img src={brand?.image} alt={brand?.brand} className="w-10 h-10 rounded-lg object-cover border border-zinc-800" />
              <div>
                <p className="font-bold text-white text-xs">{brand?.brand}</p>
                {selectedSku && <p className="text-xs text-emerald-400 mt-0.5">{selectedSku.name}</p>}
              </div>
            </div>
            <div className="space-y-3 border-t border-zinc-850 pt-4 font-mono text-xs">
              <div className="flex justify-between">
                <span className="text-zinc-500">TARGET ID</span>
                <span className="text-white font-semibold">{userId}{cfg.show_id2 && serverId ? ` (${serverId})` : ''}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">PAYMENT</span>
                <span className="text-emerald-400 font-semibold">SUCCESS</span>
              </div>
            </div>
            <div className="border-t border-zinc-850 mt-4 pt-4 flex justify-between items-center">
              <span className="text-zinc-500 text-xs font-mono">TOTAL PRICE</span>
              <span className="font-bold text-white text-md">Rp {selectedSku?.price?.toLocaleString('id-ID')}</span>
            </div>
          </div>

          {/* Buttons - only show when processing is done */}
          {!isProcessing && (
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1 border-zinc-800 text-white hover:bg-zinc-900 text-xs" onClick={() => navigate('/track')}>Check Status</Button>
              <Button className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold text-xs" onClick={() => navigate('/')}>Back to Home</Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ==================== PAYING ====================
  if (paymentStep === 'paying' && paymentData) {
    const isVA = paymentData.payment_method === 'virtual_account';
    return (
      <div className="min-h-screen pt-24 pb-12 bg-[#09090b] text-zinc-50 relative">
        <div className="absolute inset-0 bg-[radial-gradient(#1c1c1f_1px,transparent_1px)] [background-size:16px_16px] opacity-10 pointer-events-none" />
        <div className="max-w-lg mx-auto px-4 relative z-10">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center mx-auto mb-4 animate-pulse">
              <Clock className="w-8 h-8 text-cyan-400" />
            </div>
            <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full border border-cyan-500/30 bg-cyan-500/5">
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-500 animate-pulse" />
              <span className="font-mono text-[10px] text-cyan-400 font-semibold tracking-wider uppercase">Awaiting Settlement</span>
            </div>
            <h1 className="font-bold text-xl text-white mb-2">Waiting for Payment</h1>
            <p className="text-zinc-400 text-sm">Please settle the transaction invoice in the next 24 hours.</p>
          </div>

          <div className="bg-zinc-900/30 rounded-xl p-6 border border-zinc-850 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <img src={brand.image} alt={brand.brand} className="w-10 h-10 rounded-lg object-cover border border-zinc-800" />
              <div>
                <p className="font-bold text-white text-xs">{brand.brand}</p>
                {selectedSku && <p className="text-xs text-emerald-400 mt-0.5">{selectedSku.name}</p>}
              </div>
            </div>
            <div className="text-xs text-zinc-400 font-mono mb-3">
              {cfg.success_label}: <span className="text-white font-semibold">{userId}{cfg.show_id2 && serverId ? ` (${serverId})` : ''}</span>
            </div>
            <div className="border-t border-zinc-850 pt-4 flex justify-between items-center">
              <span className="text-zinc-500 text-xs font-mono">TOTAL INVOICE</span>
              <span className="font-bold text-white text-md">Rp {selectedSku?.price?.toLocaleString('id-ID')}</span>
            </div>
          </div>

          {isVA ? (
            <div className="bg-zinc-900/30 rounded-xl p-6 border border-zinc-850 mb-6">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1.5 rounded font-bold text-xs text-white"
                  style={{ backgroundColor: BANK_INFO[paymentData.channel]?.color || '#333' }}>
                  {BANK_INFO[paymentData.channel]?.name || paymentData.channel?.toUpperCase()}
                </span>
                <span className="text-white text-xs font-medium">Virtual Account Billing</span>
              </div>
              <div className="bg-zinc-950/50 rounded-lg p-4 mb-4 border border-zinc-850">
                <p className="text-[10px] text-zinc-550 mb-2 font-mono">VIRTUAL ACCOUNT NUMBER</p>
                <div className="flex items-center justify-between">
                  <span className="font-mono text-base text-white font-bold tracking-wider">{paymentData.va_number}</span>
                  <button onClick={() => copyText(paymentData.va_number)} className="p-1.5 rounded-lg bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 transition-colors border border-emerald-500/20">
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              <p className="text-xs text-zinc-400">Please transfer the exact invoice amount using your preferred mobile banking application or ATM.</p>
            </div>
          ) : (
            <div className="bg-zinc-900/30 rounded-xl p-6 border border-zinc-850 mb-6">
              <div className="flex items-center gap-3 mb-4">
                <QrCode className="w-5 h-5 text-cyan-400" />
                <span className="font-bold text-white text-xs">QRIS Code</span>
              </div>
              <div className="bg-white rounded-xl p-6 mb-4 flex justify-center border border-zinc-200">
                {paymentData.qr_content ? (
                  <QRCodeCanvas value={paymentData.qr_content} size={220} level="M" marginSize={2} />
                ) : (
                  <div className="w-[220px] h-[220px] flex items-center justify-center">
                    <QrCode className="w-32 h-32 text-gray-300" />
                  </div>
                )}
              </div>
              <p className="text-center text-xs text-zinc-500">Scan this QR code with GoPay, OVO, Dana, LinkAja or your mobile banking application.</p>
            </div>
          )}

          <Button variant="outline" className="w-full border-zinc-800 text-zinc-400 hover:text-white text-xs"
            onClick={() => { setPaymentStep('form'); setPaymentData(null); }}>
            <RefreshCw className="w-4 h-4 mr-2" /> Change Payment Method
          </Button>
          <p className="text-center text-xs text-zinc-500 mt-4 flex items-center justify-center gap-2 font-mono">
            <Loader2 className="w-3 h-3 animate-spin text-cyan-400" /> Connecting to payment provider...
          </p>
        </div>
      </div>
    );
  }

  // ==================== FORM ====================

  return (
    <div 
      className="min-h-screen bg-cover bg-center bg-[#09090b] text-zinc-50 relative pt-24 pb-16"
      style={{ backgroundImage: "url('/hero-bg.png')" }}
    >
      <div className="absolute inset-0 bg-gradient-to-b from-[#09090b]/80 via-[#09090b]/90 to-[#09090b] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Widescreen 3-Column Terminal Deck */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          
          {/* COLUMN 1: System Telemetry & Brand Manifest (col-span-3) */}
          <div className="lg:col-span-3 space-y-6">
            
            {/* Back Button & Brand Card */}
            <div className="relative overflow-hidden rounded-2xl border border-zinc-850 bg-zinc-950/75 backdrop-blur-md p-5 shadow-2xl">
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/5 via-transparent to-cyan-500/5 pointer-events-none" />
              <Button
                variant="ghost"
                size="sm"
                className="text-zinc-400 hover:text-white mb-5 text-xs transition-colors p-0 hover:bg-transparent"
                onClick={() => navigate('/')}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back to products
              </Button>
              
              <div className="flex items-center gap-4">
                <img
                  src={brand.image}
                  alt={brand.brand}
                  className="w-14 h-14 rounded-2xl object-cover border border-emerald-500/20 shadow-[0_0_20px_rgba(16,185,129,0.15)]"
                />
                <div>
                  <h1 className="font-bold text-lg text-white tracking-wide uppercase leading-tight">
                    {brand.brand}
                  </h1>
                  <span className="text-[9px] font-mono text-emerald-455 tracking-widest uppercase bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/20 mt-1 inline-block">
                    {category}
                  </span>
                </div>
              </div>
            </div>

            {/* Diagnostics Telemetry */}
            <div className="bg-zinc-950/45 backdrop-blur-md rounded-2xl p-5 border border-zinc-850 shadow-xl relative overflow-hidden font-mono text-[11px] space-y-3">
              <div className="absolute top-0 left-0 w-16 h-0.5 bg-cyan-500" />
              <div className="text-[10px] text-zinc-500 tracking-wider uppercase mb-1">SYSTEM_TELEMETRY</div>
              <div className="flex justify-between items-center border-b border-zinc-900/60 pb-1.5">
                <span className="text-zinc-400">NODE_STATUS</span>
                <span className="text-emerald-400 flex items-center gap-1.5 font-bold">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> ONLINE
                </span>
              </div>
              <div className="flex justify-between items-center border-b border-zinc-900/60 pb-1.5">
                <span className="text-zinc-400">LATENCY</span>
                <span className="text-cyan-400">18 ms</span>
              </div>
              <div className="flex justify-between items-center border-b border-zinc-900/60 pb-1.5">
                <span className="text-zinc-400">ENCRYPTION</span>
                <span className="text-zinc-350">AES-256</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-zinc-400">CORE_SYNC</span>
                <span className="text-emerald-400">ESTABLISHED</span>
              </div>
            </div>

            {/* Instructions */}
            {cfg.instruction && (
              <div className="bg-emerald-950/15 border border-emerald-500/15 rounded-2xl p-4">
                <div className="flex items-start gap-3">
                  <Zap className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5 animate-pulse" />
                  <p className="text-xs text-zinc-350 leading-relaxed font-sans">{cfg.instruction}</p>
                </div>
              </div>
            )}

            {/* Console Manifest (Sticky Order Summary) */}
            <div className="bg-zinc-950/75 backdrop-blur-md rounded-2xl p-5 border border-zinc-850 shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/5 rounded-full blur-3xl pointer-events-none" />
              
              <h2 className="font-bold text-xs text-white mb-5 uppercase tracking-widest flex items-center gap-2 border-b border-zinc-850 pb-3">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                Console Manifest
              </h2>

              <div className="space-y-4 mb-5 text-xs font-mono">
                <div>
                  <span className="text-zinc-550 tracking-wider text-[10px]">SELECTED_PRODUCT</span>
                  <p className="font-bold text-white uppercase mt-0.5">{brand.brand}</p>
                  {selectedSku ? (
                    <p className="text-emerald-400 tracking-wider text-[11px] mt-0.5">{selectedSku.name}</p>
                  ) : (
                    <p className="text-zinc-650 tracking-wider text-[11px] mt-0.5">PENDING_NOMINAL</p>
                  )}
                </div>

                {(userId || serverId) && (
                  <div className="bg-zinc-900/40 rounded-xl p-3 border border-zinc-850 space-y-1.5 text-[11px]">
                    {userId && (
                      <p className="text-zinc-400 flex justify-between">
                        <span>{cfg.id_label?.toUpperCase() || 'TARGET_ID'}:</span> 
                        <span className="text-white font-bold">{userId}</span>
                      </p>
                    )}
                    {cfg.show_id2 && serverId && (
                      <p className="text-zinc-400 flex justify-between">
                        <span>{cfg.id2_label?.toUpperCase() || 'ZONE_ID'}:</span> 
                        <span className="text-white font-bold">{serverId}</span>
                      </p>
                    )}
                  </div>
                )}

                <div className="border-t border-zinc-850 pt-3">
                  <span className="text-zinc-550 tracking-wider text-[10px]">PAYMENT_GATEWAY</span>
                  {selectedPayment ? (
                    <p className="text-white uppercase font-bold mt-0.5">{selectedPayment.name}</p>
                  ) : (
                    <p className="text-zinc-650 tracking-wider text-[11px] mt-0.5">PENDING_GATEWAY</p>
                  )}
                </div>
              </div>

              <div className="border-t border-zinc-805 pt-4 mb-5">
                <div className="flex justify-between items-center">
                  <span className="text-[10px] text-zinc-400 font-mono tracking-wider">TOTAL_ESTIMATE</span>
                  <span className="text-lg font-extrabold text-white font-mono">
                    {selectedSku ? `Rp ${selectedSku.price.toLocaleString('id-ID')}` : 'Rp 0'}
                  </span>
                </div>
              </div>
            </div>

          </div>

          {/* COLUMN 2: Select Synapse Nominal (col-span-5) */}
          <div className="lg:col-span-5 space-y-6">
            <div className="bg-zinc-900/40 backdrop-blur-md rounded-2xl p-6 border border-zinc-800 shadow-xl relative overflow-hidden">
              <div className="absolute top-0 left-0 w-24 h-1 bg-gradient-to-r from-emerald-500 to-transparent" />
              <h2 className="font-bold text-sm text-white mb-6 uppercase tracking-widest flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-[10px] flex items-center justify-center font-mono">01</span>
                Select Synapse Nominal
              </h2>
              <div className="grid grid-cols-2 gap-4">
                {products.map((p) => {
                  const isSelected = selectedSku?.sku === p.sku;
                  return (
                    <button
                      key={p.sku}
                      className={`p-4 rounded-xl text-left border transition-all duration-300 relative overflow-hidden flex flex-col justify-between min-h-[96px] ${
                        isSelected
                          ? 'border-emerald-500/80 bg-emerald-500/5 shadow-[0_0_15px_rgba(16,185,129,0.15)]'
                          : 'border-zinc-800 bg-zinc-950/60 hover:border-zinc-700 hover:bg-zinc-900/30'
                      }`}
                      onClick={() => setSelectedSku(p)}
                      data-testid={`sku-${p.sku}`}
                    >
                      {isSelected && (
                        <div className="absolute top-0 right-0 w-8 h-8 bg-emerald-500/10 border-b border-l border-emerald-500/20 rounded-bl-lg flex items-center justify-center">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                        </div>
                      )}
                      <p className="font-bold text-white text-xs tracking-wider uppercase">{p.name}</p>
                      <p className="text-md font-extrabold text-emerald-400 font-mono tracking-wide mt-2">
                        Rp {p.price.toLocaleString('id-ID')}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* COLUMN 3: Identity, Payment Gateways & Submit Deck (col-span-4) */}
          <div className="lg:col-span-4 space-y-6">
            
            {/* Identity Gateway */}
            <div className="bg-zinc-900/40 backdrop-blur-md rounded-2xl p-6 border border-zinc-800 shadow-xl relative overflow-hidden">
              <div className="absolute top-0 left-0 w-24 h-1 bg-gradient-to-r from-emerald-500 to-transparent" />
              <h2 className="font-bold text-sm text-white mb-6 uppercase tracking-widest flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-[10px] flex items-center justify-center font-mono">02</span>
                Target Identity Gateway
              </h2>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-[10px] font-mono text-zinc-400 tracking-widest uppercase">{cfg.id_label}</Label>
                  <Input 
                    placeholder={cfg.id_placeholder} 
                    className="bg-zinc-950/80 border-zinc-800 text-white focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/40 text-sm rounded-lg h-12 px-4 transition-all"
                    value={userId} 
                    onChange={(e) => setUserId(e.target.value)} 
                    data-testid="df-user-id" 
                  />
                </div>
                {cfg.show_id2 && (
                  <div className="space-y-2">
                    <Label className="text-[10px] font-mono text-zinc-400 tracking-widest uppercase">{cfg.id2_label}</Label>
                    <Input 
                      placeholder={cfg.id2_placeholder} 
                      className="bg-zinc-950/80 border-zinc-800 text-white focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/40 text-sm rounded-lg h-12 px-4 transition-all"
                      value={serverId} 
                      onChange={(e) => setServerId(e.target.value)} 
                      data-testid="df-server-id" 
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Select Gateway */}
            <div className="bg-zinc-900/40 backdrop-blur-md rounded-2xl p-6 border border-zinc-800 shadow-xl relative overflow-hidden">
              <div className="absolute top-0 left-0 w-24 h-1 bg-gradient-to-r from-cyan-500 to-transparent" />
              <h2 className="font-bold text-sm text-white mb-6 uppercase tracking-widest flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-[10px] flex items-center justify-center font-mono">03</span>
                Select Synapse Gateway
              </h2>
              <div className="space-y-3">
                {PAYMENT_METHODS.map((method) => {
                  const Icon = method.icon;
                  const isSelected = selectedPayment?.id === method.id;
                  return (
                    <button
                      key={method.id}
                      className={`w-full p-3.5 rounded-xl flex items-center gap-4 border transition-all duration-300 ${
                        isSelected
                          ? 'border-cyan-500/80 bg-cyan-500/5 shadow-[0_0_15px_rgba(6,182,212,0.15)]'
                          : 'border-zinc-800 bg-zinc-950/60 hover:border-zinc-700 hover:bg-zinc-900/30'
                      }`}
                      onClick={() => setSelectedPayment(method)}
                      data-testid={`pay-${method.id}`}
                    >
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 border transition-colors ${
                        isSelected ? 'bg-cyan-500/10 border-cyan-500/30' : 'bg-zinc-900 border-zinc-800'
                      }`}>
                        <Icon className={`w-4 h-4 ${isSelected ? 'text-cyan-400' : 'text-zinc-400'}`} />
                      </div>
                      <div className="text-left">
                        <p className="font-bold text-white text-xs tracking-wider uppercase leading-none">{method.name}</p>
                        <p className="text-[10px] text-zinc-500 mt-1 leading-normal font-sans">{method.desc}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Email Gateway */}
            <div className="bg-zinc-900/40 backdrop-blur-md rounded-2xl p-6 border border-zinc-800 shadow-xl relative overflow-hidden">
              <div className="absolute top-0 left-0 w-24 h-1 bg-gradient-to-r from-emerald-500 to-transparent" />
              <h2 className="font-bold text-sm text-white mb-6 uppercase tracking-widest flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-[10px] flex items-center justify-center font-mono">04</span>
                Receipt Gateway
              </h2>
              <div className="space-y-3">
                <Label htmlFor="email" className="text-[10px] font-mono text-zinc-400 tracking-widest uppercase">Email Address</Label>
                <Input 
                  id="email"
                  type="email" 
                  placeholder="Enter email for receipt" 
                  className="bg-zinc-950/80 border-zinc-800 text-white focus:border-emerald-500 focus:ring-1 focus:ring-emerald-550/40 text-sm rounded-lg h-12 px-4 transition-all"
                  value={email} 
                  onChange={(e) => setEmail(e.target.value)} 
                  data-testid="df-email" 
                />
              </div>
            </div>

            {/* Bayar Sekarang Button */}
            <Button
              className="w-full bg-emerald-500 hover:bg-emerald-400 hover:shadow-[0_0_20px_rgba(16,185,129,0.25)] text-zinc-950 font-extrabold py-6 rounded-xl text-xs tracking-widest uppercase transition-all duration-300"
              onClick={handleSubmit}
              disabled={submitting || !selectedSku || !selectedPayment}
              data-testid="df-order-btn"
            >
              {submitting ? (
                <span className="flex items-center gap-2 justify-center font-sans">
                  <Loader2 className="w-4 h-4 animate-spin text-zinc-950" />
                  TRANSACTING...
                </span>
              ) : (
                <span className="flex items-center gap-2 justify-center font-sans">
                  <Zap className="w-4 h-4 fill-current animate-pulse" />
                  Bayar Sekarang
                </span>
              )}
            </Button>

          </div>

        </div>
      </div>
    </div>
  );
}
